import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Sun, Moon } from "lucide-react";
import { useTheme } from "../lib/theme";
import { useI18n } from "../lib/i18n";

export default function Nav() {
  const { dark, toggle } = useTheme();
  const { d } = useI18n();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const LINKS = [
    { label: d.nav.pillars, href: "/#pillars", testid: "nav-pillars-link" },
    { label: d.nav.network, href: "/#network", testid: "nav-network-link" },
    { label: d.nav.wrd, href: "/world-rhubarb-day", testid: "nav-wrd-link" },
    { label: d.nav.knowledge, href: "/#knowledge", testid: "nav-knowledge-link" },
    { label: d.nav.transparency, href: "/transparency", testid: "nav-transparency-link" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    if (!href.startsWith("/#")) {
      setOpen(false);
      return;
    }
    e.preventDefault();
    const id = href.slice(2);
    setOpen(false);
    if (location.pathname === "/") {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 120);
    }
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "border-b border-border bg-background/85 backdrop-blur-xl" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 sm:px-10 lg:px-16">
        <Link to="/" data-testid="nav-brand-link" className="group flex items-center gap-3" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
          <span className="flex h-9 w-9 items-center justify-center border border-foreground/30 font-serif text-lg italic transition-colors group-hover:border-crimson group-hover:text-crimson">
            R
          </span>
          <span className="leading-tight">
            <span className="block font-mono text-[10px] uppercase tracking-[0.3em]">{d.nav.brandTop}</span>
            <span className="block font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{d.nav.brandBottom}</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-7 lg:flex">
          {LINKS.map((l) =>
            l.href.startsWith("/#") ? (
              <a key={l.href} href={l.href} data-testid={l.testid} onClick={(e) => go(e, l.href)} className="u-link font-mono text-[11px] uppercase tracking-[0.22em] text-foreground/80 hover:text-foreground">
                {l.label}
              </a>
            ) : (
              <Link key={l.href} to={l.href} data-testid={l.testid} className="u-link font-mono text-[11px] uppercase tracking-[0.22em] text-foreground/80 hover:text-foreground">
                {l.label}
              </Link>
            )
          )}
          <button
            onClick={toggle}
            data-testid="theme-toggle-button"
            aria-label="Toggle dark mode"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-foreground/20 transition-colors hover:border-crimson hover:text-crimson"
          >
            {dark ? <Sun size={15} /> : <Moon size={15} />}
          </button>
          <a
            href="/#join"
            data-testid="nav-join-cta"
            onClick={(e) => go(e, "/#join")}
            className="border border-crimson bg-crimson px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.22em] text-parchment transition-colors hover:bg-transparent hover:text-crimson"
          >
            {d.nav.join}
          </a>
        </nav>

        <div className="flex items-center gap-3 lg:hidden">
          <button onClick={toggle} data-testid="theme-toggle-button-mobile" aria-label="Toggle dark mode" className="flex h-9 w-9 items-center justify-center rounded-full border border-foreground/20">
            {dark ? <Sun size={15} /> : <Moon size={15} />}
          </button>
          <button onClick={() => setOpen(!open)} data-testid="nav-menu-button" aria-label="Open menu" className="flex h-9 w-9 items-center justify-center border border-foreground/20">
            {open ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-border bg-background/95 backdrop-blur-xl lg:hidden" data-testid="nav-mobile-menu">
          <div className="flex flex-col gap-1 px-6 py-6">
            {LINKS.map((l) =>
              l.href.startsWith("/#") ? (
                <a key={l.href} href={l.href} data-testid={`${l.testid}-mobile`} onClick={(e) => go(e, l.href)} className="py-3 font-mono text-xs uppercase tracking-[0.22em]">
                  {l.label}
                </a>
              ) : (
                <Link key={l.href} to={l.href} data-testid={`${l.testid}-mobile`} onClick={() => setOpen(false)} className="py-3 font-mono text-xs uppercase tracking-[0.22em]">
                  {l.label}
                </Link>
              )
            )}
            <a href="/#join" data-testid="nav-join-cta-mobile" onClick={(e) => go(e, "/#join")} className="mt-3 border border-crimson bg-crimson px-5 py-3 text-center font-mono text-xs uppercase tracking-[0.22em] text-parchment">
              {d.nav.join}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
